import { FC, useEffect, createRef, useState } from 'react';
import styled from 'styled-components';
import { themeColors, themeSizes } from '../../../styles/globalStyleVars';
import {
  IconAlertTriangleFilled,
  IconBook2,
  IconChevronDown,
  IconChevronUp,
} from '@tabler/icons-react';
import RelativeTime from '../../RelativeTime/RelativeTime';
import { AffectsMeButton } from './AffectsMeButton';
import { AlertInterface } from '../../../interfaces/AlertInterface';
import { callEvent } from '../../../utils/Amplitude/AmplitudeUtilities';
import { TypographyLineLimit } from '../../Styled/TypographyElements';
import { RealTimeUpdate } from './RealTimeUpdate';
import { TypographyStyled } from '../../Styled/Typography';
import { Button, Divider, LinkButton } from '@cfa/react-core';

interface Props {
  alert: AlertInterface;
  setAlertChildCaseOpen?: (val: AlertInterface | undefined) => void;
  alertsDrawerOpen: boolean;
  activeLocation?: string;
  showSummaryByDefault?: boolean;
}

const AlertDiv = styled.div`
  background-color: ${themeColors.primaryWhite};
  border: 1px solid ${themeColors.colorGray_2};
  border-radius: ${themeSizes.sm};
  width: 100%;
  margin-bottom: ${themeSizes.lg};
  padding: ${themeSizes.lg};
  display: flex;
  flex-direction: column;
`;

const AlertStatus = styled.div`
  display: flex;
  justify-content: space-between;

  > div {
    color: ${themeColors.errorColor};
    display: flex;
    margin-bottom: ${themeSizes.smMd};

    > svg {
      margin-right: ${themeSizes.sm};
    }

    > p {
      color: ${themeColors.errorColor};
    }
  }
`;

const AlertSummaryText = styled(TypographyStyled)`
  color: ${themeColors.colorBlack};
  margin-top: 9px;
  white-space: pre-line;
`;

const SeeMoreButton = styled(Button)`
  padding: 0;
  display: flex;
  display: -webkit-flex;
  justify-content: right;
  text-decoration: none;
  -webkit-justify-content: right;
  -webkit-align-self: flex-end;
`;

const LinkToURLButton = styled(LinkButton)`
  padding: 0;
  min-width: auto;
  margin-top: ${themeSizes.sm};

  > svg {
    margin-right: ${themeSizes.sm};
  }
`;

export const AlertItem: FC<Props> = ({
  alert,
  setAlertChildCaseOpen,
  alertsDrawerOpen,
  activeLocation,
  showSummaryByDefault,
}) => {
  const [overflowingSummary, setOverflowingSummary] = useState<boolean>(false);
  const [expandedSummary, setExpandedSummary] = useState<boolean>(false);
  const summaryRef = createRef<HTMLParagraphElement>();

  useEffect(() => {
    if (alertsDrawerOpen) {
      if (summaryRef && summaryRef.current) {
        if (
          summaryRef?.current?.clientHeight &&
          summaryRef?.current?.clientHeight > 240
        ) {
          setOverflowingSummary(true);
        }
      }
    } else {
      //wait until after drawer closes (225 ms animation is done) to clear out state
      const timeout = setTimeout(() => {
        setOverflowingSummary(false);
        setExpandedSummary(false);
      }, 225);

      return () => clearTimeout(timeout);
    }
    return;
  }, [alertsDrawerOpen]);

  const handleExpandSummary = (id: string, title?: string | null) => {
    if (expandedSummary) {
      setExpandedSummary(false);
    } else {
      setExpandedSummary(true);
      callEvent('expand alert', {
        'alert id': id,
        title: title,
      });
    }
  };

  const handleAffectsMeClick = () => {
    if (setAlertChildCaseOpen) {
      setAlertChildCaseOpen(alert);
    }
    callEvent('select this affects my restaurant', {
      'alert id': alert.id,
      id: alert.majorCaseId as string,
      location: 'drawer',
    });
  };

  return (
    <AlertDiv key={alert.id} className='alert-item'>
      <AlertStatus>
        <div>
          <IconAlertTriangleFilled
            size={26}
            aria-label='Warning triangle icon'
          />
        </div>
        <TypographyStyled data-testid='alert-time'>
          <RelativeTime date={alert.updatedOn} />
        </TypographyStyled>
      </AlertStatus>
      <TypographyLineLimit
        $fontColor={themeColors.colorBlack}
        $lineLimit={3}
        variant='h4'
        style={{ maxHeight: '96px' }}
        data-testid='alert-heading'
      >
        {alert.title}
      </TypographyLineLimit>

      {showSummaryByDefault ? (
        <>
          {overflowingSummary && !expandedSummary ? (
            <TypographyLineLimit
              $fontColor={themeColors.colorBlack}
              $lineLimit={10}
              style={{
                maxHeight: '240px',
                marginTop: '9px',
                whiteSpace: 'pre-line',
              }}
              data-testid='alert-summary'
            >
              {alert.summary}
            </TypographyLineLimit>
          ) : (
            <AlertSummaryText
              ref={summaryRef}
              text={alert.summary ?? ''}
              data-testid='alert-summary'
            />
          )}

          {overflowingSummary && (
            <SeeMoreButton
              variant='text'
              color='secondary'
              size='sm'
              onPress={() => handleExpandSummary(alert.id, alert.title)}
            >
              {expandedSummary ? 'See Less' : 'See More'}
            </SeeMoreButton>
          )}
          {(alert.majorCaseId || alert.url || alert.outagePageURL) && (
            <Divider
              style={{
                margin: `${themeSizes.smMd} 0 ${
                  alert.outagePageURL ? '6px' : themeSizes.md
                }`,
              }}
            />
          )}
          {alert.outagePageURL && (
            <RealTimeUpdate
              alert={alert}
              activeLocation={activeLocation ?? ''}
            />
          )}
          {alert.majorCaseId && (
            <AffectsMeButton onClick={() => handleAffectsMeClick()} />
          )}
          {alert.url && (
            <LinkToURLButton
              variant='text'
              color='secondary'
              href={alert.url}
              target='_blank'
              rel='noreferrer'
              onPress={() =>
                callEvent('alert link', {
                  'alert id': alert?.id,
                  title: alert.title,
                })
              }
            >
              <IconBook2 size={16} />
              <TypographyStyled
                text='Read More'
                fontWeight='bold'
                color={themeColors.secondaryBlue}
              />
            </LinkToURLButton>
          )}
        </>
      ) : (
        alert.summary && (
          <>
            {expandedSummary && (
              <AlertSummaryText
                text={alert.summary ?? ''}
                data-testid='alert-summary'
              />
            )}
            <SeeMoreButton
              variant='text'
              color='secondary'
              size='sm'
              onPress={() => handleExpandSummary(alert.id, alert.title)}
            >
              {expandedSummary ? 'Hide' : 'Show'} details{' '}
              {expandedSummary ? (
                <IconChevronUp
                  size={themeSizes.lg}
                  style={{ margin: `0 0 0 ${themeSizes.xs}` }}
                />
              ) : (
                <IconChevronDown
                  size={themeSizes.lg}
                  style={{ margin: `0 0 0 ${themeSizes.xs}` }}
                />
              )}
            </SeeMoreButton>
          </>
        )
      )}
    </AlertDiv>
  );
};
